/**
 * AppVersionBadge — reads the currently-loaded Capgo bundle version via the
 * CapacitorUpdater plugin (falls back to package.json version when running on
 * the web / preview). Drop anywhere (footer, settings, about page) to give
 * users + yourself a live visual proof that a new OTA bundle landed.
 *
 * Format: "v4.0.2 · updated Apr 21"
 */
import React, { useEffect, useState } from "react";
import BUILD_INFO from "../../buildInfo";
import { API } from "../../utils";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return `${MONTHS[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
};

export default function AppVersionBadge({ className = "", compact = false, inline = false }) {
  const [label, setLabel] = useState(null);
  const [raw, setRaw] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      let version = null;
      let updated = null;
      let native = false;
      let bundleId = null;
      try {
        // Try Capgo plugin first — only works on native
        const mod = await import("@capgo/capacitor-updater");
        const res = await mod.CapacitorUpdater.current();
        if (res?.bundle) {
          version = res.bundle.version;
          // Capgo returns "0" string for builtin bundle timestamps on web — ignore them
          const u = res.bundle.downloaded || res.bundle.last_used || res.bundle.date;
          if (u && String(u) !== "0" && !String(u).startsWith("1970")) {
            updated = u;
          }
          bundleId = res.bundle.id || null;
          native = !!(updated || res.bundle.id);
        }
      } catch { /* not native / plugin not installed — fall through */ }

      // On web (no Capgo native), ask the backend what it was deployed with —
      // this lets the live website show the correct version + deploy date
      // automatically after every redeploy, no manual file edits needed.
      if (!native && (!version || !updated)) {
        try {
          const res = await fetch(`${API}/system/version`);
          if (res.ok) {
            const data = await res.json();
            if (data?.version) version = version || data.version;
            if (data?.buildDate) updated = updated || data.buildDate;
          }
        } catch { /* backend unreachable — fall through to bundled values */ }
      }

      // Final fallback: the auto-stamped buildInfo.js (regenerated on every
      // `npm run build` by scripts/gen-buildinfo.js). Covers full-offline mode.
      if (!version) {
        version = BUILD_INFO.version || "0.0.0";
      }
      if (!updated || updated === "") updated = BUILD_INFO.buildDate;
      if (!alive) return;
      const suffix = fmt(updated);
      setLabel(`v${version}${suffix ? " · updated " + suffix : ""}`);
      setRaw({ version, updated, native, bundleId });
    })();
    return () => { alive = false; };
  }, []);

  const onCopy = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!raw) return;
    const diag = `BLK PACKS v${raw.version}\nupdated: ${new Date(raw.updated).toISOString()}\nsource: ${raw.native ? "Capgo OTA" : "web"}\nbundle: ${raw.bundleId || "—"}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(diag);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };

  if (!label) return null;

  if (inline) {
    return (
      <button
        type="button"
        onClick={onCopy}
        className={`brand-gradient-text font-bold hover:brightness-110 transition ${className}`}
        data-testid="app-version-badge"
        title="Tap to copy build info for support"
      >
        {copied ? "COPIED ✓" : label}
      </button>
    );
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={onCopy}
        className={`text-[10px] uppercase tracking-[0.2em] text-gray-500 font-mono hover:text-emerald-300 transition-colors ${className}`}
        data-testid="app-version-badge"
        title="Tap to copy build info for support"
      >
        {copied ? "COPIED ✓" : label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      className={`inline-flex items-center gap-1.5 rounded-full bg-black/40 border border-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gray-400 font-mono hover:text-emerald-300 hover:border-emerald-500/40 transition-colors ${className}`}
      data-testid="app-version-badge"
      title="Tap to copy build info for support"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${copied ? "bg-emerald-400" : "bg-emerald-500 animate-pulse"}`} aria-hidden />
      {copied ? "COPIED — paste in chat to verify" : label}
    </button>
  );
}
